import type { Trip, ThemeClasses, ViewType } from "../types";
import { TripGridCard } from "./TripGridCard";
import { TripListRow } from "./TripListRow";
import type { TFunction } from "i18next";
import { EmptyState } from "../../../../../../components/ui";

interface TripListProps {
  trips: Trip[];
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onAssignments: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
  /**
   * Eleva al padre la petición de abrir el modal de chat del viaje. Se
   * resuelve en `TripsSection` para que el modal sobreviva al spinner
   * full-page que esta lista pinta cuando `useTripsStore` está cargando
   * (el propio modal dispara un `fetchTrips`, ver `TripGridCard`).
   */
  onOpenChat: (trip: Trip) => void;
  /** Cotizador de costos operativos; ausente cuando la app no está habilitada. */
  onQuote?: (trip: Trip) => void;
  selectedView: ViewType;
  t: TFunction;
  loading: boolean;
  disableCRUD: boolean;
}

export function TripList({
  trips,
  themeClasses,
  onEdit,
  onDetails,
  onAssignments,
  onDelete,
  onOpenChat,
  onQuote,
  selectedView,
  t,
  loading,
  disableCRUD,
}: TripListProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (!trips.length) {
    return (
      <EmptyState
        icon="ri-route-line"
        title={t("trips.noTripsFound")}
        description={t(
          "trips.empty.description",
          "Los viajes que registres o programes aparecerán aquí.",
        )}
      />
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {trips.map((trip) => (
          <TripGridCard
            key={trip.id}
            trip={trip}
            themeClasses={themeClasses}
            onEdit={onEdit}
            onDetails={onDetails}
            onAssignments={onAssignments}
            onDelete={onDelete}
            onOpenChatModal={onOpenChat}
            onQuote={onQuote}
            t={t}
            disableCRUD={disableCRUD}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {trips.map((trip) => (
        <TripListRow
          key={trip.id}
          trip={trip}
          themeClasses={themeClasses}
          onEdit={onEdit}
          onDetails={onDetails}
          onAssignments={onAssignments}
          onDelete={onDelete}
          onOpenChatModal={onOpenChat}
          onQuote={onQuote}
        />
      ))}
    </div>
  );
}
